import React, { useEffect, useState, useContext } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { FiDownload, FiInfo } from "react-icons/fi";
import {
  Loader,
  CommonTable,
  DownloadExcelBill,
} from "../../Components/Common";
import { RESET_PAGINATION } from "../../Constants/Strings";
import * as attendanceService from "../../Services/attendanceService";
import { getUniqueDataById, formatDateDDMMYYYY } from "../../Utils";
import * as route from "../../routes";
import {
  MESSAGES,
  ATTENDANCE_COLUMNS,
  ATTENDANCE_LABELS,
  ADMIN_ATTENDANCE_TABLE_COLUMNS,
  ADMIN_ATTENDANCE_COLUMN_LABELS,
  APPROVED,
  REJECTED,
  PENDING_L1_APPROVAL,
  PENDING_L2_APPROVAL,
  EXCEL_FILE_COLUMNS,
  AUTO_APPROVED,
  CORRECTED,
} from "./constants";

import AttendanceForm from "./Attendance";
import MobileAttendanceCard from "./Component/MobileAttendanceCard";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getTimestampForFilename } from "../../Utils/timeUtils";
import { UserContext } from "../../Context";
import { ROLES } from "../../Constants/Strings";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import { getAllUsers } from "../../Services/UserService";
import { getSites } from "../../Services/siteService";
import { FiFilter } from "react-icons/fi";

const ListAttendance = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHR = user?.role === ROLES.HR || user?.role === ROLES.DIRECTOR_MANAGEMENT.toUpperCase();
  

  const [filters, setFilters] = useState({
    user: "",
    site: "",
    date: "",
    status: "",
    is_backdated:""
  });

  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(null);
  const [selectedSiteObj, setSelectedSiteObj] = useState(null);

  const fetchUsers = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getAllUsers({ search });
    return res?.data?.results || [];
  };

  const fetchSites = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getSites({ search });
    return res?.data?.results || [];
  };

  const getPaginationData = () => {
    if (pagination.offset > pagination.count) return;
    const finalParams = { ...pagination, ...filters };
    getAttendanceOverview(finalParams, false);
  };

  const getAttendanceOverview = async (finalParams, isInitial) => {
    try {
      const paramsWithFilters = {
        ...finalParams,
        ...filters,
      };

      const response =
        await attendanceService.getAttendanceList(paramsWithFilters);

      if (response.status === 200) {
        if (isInitial) {
          setAttendance(response.data.results);
        } else {
          setAttendance((prevData) =>
            getUniqueDataById(prevData, response.data.results),
          );
        }

        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAttendance([]);
    setPagination(RESET_PAGINATION);
    setLoading(true);

    getAttendanceOverview({ ...RESET_PAGINATION }, true);
  }, [filters]);

  const renderFields = (key, rowData) => {
    const value = rowData[key];

    if (key === "status") {
      switch (rowData.status) {
        case APPROVED:
          return <span className="text-green-600 font-medium">Approved</span>;
        case AUTO_APPROVED:
          return (
            <span className="text-green-600 font-medium">Auto Approved</span>
          );
        case REJECTED:
          return <span className="text-red-600 font-medium">Rejected</span>;
        case PENDING_L1_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending Approval
            </span>
          );
        case PENDING_L2_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending L2 Approval
            </span>
          );
        case CORRECTED:
          return <span className="text-blue-600 font-medium">Corrected</span>;
        default:
          return <span className="text-gray-500">Pending Approval</span>;
      }
    }

    if (
      ["checkin_time", "checkout_time"].includes(key) &&
      typeof value === "string"
    ) {
      return value.split(".")[0];
    }

    if (rowData.is_backdated === "Yes") {
      if (key === "checkin_photo_url" || key === "checkout_photo_url") {
        return "NA";
      }
    }

    switch (key) {
      case "is_backdated":
        return rowData.is_backdated === "Yes" ? (
          <span className="text-primary font-semibold">Yes</span>
        ) : <span className="text-grey-600 font-base">No</span>;
      case "status":
        return (
          <span>
            {rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)}
          </span>
        );
      case "actions":
        return (
          <button
            title="View"
            className="text-primary hover:text-secondary mb-1"
            onClick={() =>
              navigate(route.VIEW_DETAILED_PAGE.replace(":id", rowData.id), {
                state: {
                  from: location.pathname,
                },
              })
            }
          >
            <FiInfo className="text-lg" />
          </button>
        );

      case "site_name":
        return rowData.site_name || "Non-Site";

      case "date":
        return formatDateDDMMYYYY(rowData.date) || rowData.date;

      default:
        return value;
    }
  };

  useEffect(() => {
    getAttendanceOverview({ ...RESET_PAGINATION }, true);
  }, []);

  const downloadExcelData = () => {
    DownloadExcelBill({
      fetchFn: attendanceService.getAttendanceList,
      fieldMappings: EXCEL_FILE_COLUMNS,
      fileName: `Attendance_Data_${getTimestampForFilename()}.xlsx`,
    });
  };

  return (
    <div className="px-4 my-4">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="text-xl md:text-2xl text-primary font-semibold mb-3">
          Attendance View
        </div>

        <div className="flex items-center gap-3">
          <div className="flex justify-between items-center mb-4">
            <button
              className=" px-4 py-2.5 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => {
                setTempFilters(filters);
                setShowFilter(true);
              }}
            >
              <FiFilter />
            </button>
          </div>

          <div className="flex justify-between items-center mb-4">
            <button
              className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => downloadExcelData()}
            >
              <FiDownload className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Section for history */}
      {loading ? (
        <Loader />
      ) : isOpen ? (
        // Update the list after adding the attendance from child
        <AttendanceForm onClose={() => setIsOpen(false)} />
      ) : attendance ? (
        <>
          {showFilter && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
                {/* USER FILTER */}
                {(isHR) && (
                  <SearchableDropdown
                    label="User"
                    placeholder="Search user"
                    fetchFunction={fetchUsers}
                    labelKey="full_name"
                    valueKey="id"
                    includeAllOption
                    value={selectedUserObj}
                    onSelect={(user) => {
                      setSelectedUserObj(user);
                      setTempFilters((prev) => ({
                        ...prev,
                        user: user?.id || "",
                      }));
                    }}
                  />
                )}

                {/* STATUS FILTER */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={tempFilters.status || ""}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  >
                    <option value="">All</option>
                    <option value={APPROVED}>Approved</option>
                    <option value={AUTO_APPROVED}>Auto Approved</option>
                    <option value={REJECTED}>Rejected</option>
                    <option value={PENDING_L1_APPROVAL}>
                      Pending Approval
                    </option>
                    <option value={CORRECTED}>Corrected</option>
                  </select>
                </div>

                {/* Backdated  */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Backdated
                    </label>

                    <select
                      value={tempFilters.is_backdated || ""}
                      onChange={(e) =>
                        setTempFilters((prev) => ({
                          ...prev,
                          is_backdated: e.target.value,
                        }))
                      }
                      className="w-full min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                {/* SITE FILTER */}
                <SearchableDropdown
                  label="Site"
                  placeholder="Search site"
                  fetchFunction={fetchSites}
                  labelKey="name"
                  valueKey="id"
                  includeAllOption
                  value={selectedSiteObj}
                  onSelect={(site) => {
                    setSelectedSiteObj(site);
                    setTempFilters((prev) => ({
                      ...prev,
                      site: site?.id || "",
                    }));
                  }}
                />

                {/* DATE FILTER */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={tempFilters.date || ""}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex justify-between items-center pt-4">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200"
                    onClick={() => {
                      const cleared = { user: "", site: "", date: "", status: "", is_backdated: "",};
                      setTempFilters(cleared);
                      setFilters(cleared);
                      setSelectedUserObj(null);
                      setSelectedSiteObj(null);
                      setShowFilter(false);
                    }}
                  >
                    Clear
                  </button>

                  <div className="flex gap-2">
                    <button
                      className="px-4 py-2 rounded-lg bg-gray-200"
                      onClick={() => {
                        setTempFilters(filters);
                        setShowFilter(false);
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="px-4 py-2 rounded-lg bg-primary text-white"
                      onClick={() => {
                        setFilters(tempFilters);
                        setShowFilter(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={attendance.length}
              next={getPaginationData}
              hasMore={pagination.offset <= pagination.count}
              scrollableTarget="scrollableDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto md:border md:rounded-md"
                id="scrollableDiv"
              >
                <CommonTable
                  tableColumns={
                    (isHR ) ? ADMIN_ATTENDANCE_TABLE_COLUMNS : ATTENDANCE_COLUMNS
                  }
                  columnLabels={
                    (isHR) ? ADMIN_ATTENDANCE_COLUMN_LABELS : ATTENDANCE_LABELS
                  }
                  tableRows={attendance}
                  className={""}
                  customError={MESSAGES.NO_REQUESTS_AVAILABLE}
                  renderField={renderFields}
                />
              </div>
            </InfiniteScroll>
          </div>

          <div className="block md:hidden">
            <InfiniteScroll
              dataLength={attendance.length}
              next={getPaginationData}
              hasMore={pagination.offset <= pagination.count}
              scrollableTarget="mobileScrollableDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto"
                id="mobileScrollableDiv"
              >
                {attendance.map((item) => (
                  <MobileAttendanceCard key={item.id} data={item} />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        </>
      ) : (
        <div />
      )}
    </div>
  );
};

export default ListAttendance;
